import { useState, useRef, useCallback, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    TextField,
    Stack,
    Chip,
    IconButton,
} from '@mui/material';
import {
    Description as LogIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import { useGetLogQuery } from '@/store/apiSlice';
import { useI18nContext } from '@/i18n/i18n-react';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { RefetchButton } from '@/components/admin/RefetchButton';

type LogLevel = 'error' | 'warning' | 'info' | 'debug' | 'default';

const AdminLogsPage = () => {
    const { LL } = useI18nContext();
    const LIMIT = 50;
    const [offset, setOffset] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [allLogLines, setAllLogLines] = useState<string[]>([]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const lastProcessedOffsetRef = useRef<number>(-1);
    const [activeFilters, setActiveFilters] = useState<Set<LogLevel>>(
        new Set(['error', 'warning', 'default'])
    );

    const {
        data: logData,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useGetLogQuery({
        offset,
        limit: LIMIT,
        levels: Array.from(activeFilters).join(','),
    });

    const totalLines = logData?.total ?? 0;
    const logLines = logData?.lines ?? [];

    const handleRefetch = useCallback(() => {
        setOffset(0);
        setAllLogLines([]);
        lastProcessedOffsetRef.current = -1;
        refetch();
    }, [refetch]);

    // Reset logs when filters change
    useEffect(() => {
        setOffset(0);
        setAllLogLines([]);
        lastProcessedOffsetRef.current = -1;
    }, [activeFilters]);

    // Accumulate log lines as pages load
    useEffect(() => {
        if (!isFetching && logLines.length > 0) {
            // Only process if we haven't processed this offset yet
            if (lastProcessedOffsetRef.current !== offset) {
                lastProcessedOffsetRef.current = offset;

                if (offset === 0) {
                    setAllLogLines(logLines);
                } else {
                    setAllLogLines((prev) => [...prev, ...logLines]);
                }
            }

            // Reset loading state after processing
            if (isLoadingMore) {
                setIsLoadingMore(false);
            }
        } else if (!isFetching && logLines.length === 0 && isLoadingMore) {
            // Handle empty response (end of logs)
            setIsLoadingMore(false);
        }
    }, [logLines, offset, isFetching, isLoadingMore]);

    const hasMoreLogs = allLogLines.length < totalLines;

    const filteredLines = allLogLines.filter((line) => {
        // Filter by search query (severity filtering is done server-side)
        if (!searchQuery.trim()) return true;
        return line.toLowerCase().includes(searchQuery.toLowerCase());
    });

    // Handle infinite scroll
    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container || isLoadingMore || !hasMoreLogs || isFetching) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const scrolledToBottom = scrollHeight - scrollTop - clientHeight < 100;

        if (scrolledToBottom) {
            setIsLoadingMore(true);
            setOffset((prev) => prev + LIMIT);
        }
    }, [hasMoreLogs, isFetching, isLoadingMore, LIMIT]);

    // Parse log level from line (assuming format like [2024-01-01 12:00:00] [INFO] Message)
    const getLogLevel = (line: string): LogLevel => {
        if (line.includes('[ERROR]') || line.includes('[CRITICAL]') || line.includes('[FATAL]')) {
            return 'error';
        }
        if (line.includes('[WARNING]') || line.includes('[WARN]')) {
            return 'warning';
        }
        if (line.includes('[DEBUG]')) {
            return 'debug';
        }
        if (line.includes('[INFO]')) {
            return 'info';
        }
        return 'default';
    };

    return (
        <>
            <AdminPageHeader
                icon={<LogIcon />}
                title={LL.adminLogsPage.title()}
                subtitle={LL.adminLogsPage.subtitle()}
                refetchButton={
                    <RefetchButton onRefetch={handleRefetch} isRefetching={isFetching} />
                }
            />

            <Box sx={{ px: 3 }}>
                {/* Search and Stats */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{ mb: 3, alignItems: { xs: 'stretch', sm: 'center' } }}
                >
                    <TextField
                        size="small"
                        placeholder={LL.adminLogsPage.searchPlaceholder()}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                ),
                                endAdornment: searchQuery && (
                                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                ),
                            },
                        }}
                    />
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Chip
                            label={LL.adminLogsPage.linesCount({
                                count: filteredLines.length,
                            })}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                        {(searchQuery || activeFilters.size < 4) && (
                            <Chip
                                label={LL.adminLogsPage.filteredFrom({
                                    total: allLogLines.length,
                                })}
                                size="small"
                                color="default"
                                variant="outlined"
                            />
                        )}
                        {!searchQuery &&
                            activeFilters.size >= 4 &&
                            totalLines > allLogLines.length && (
                                <Chip
                                    label={`${allLogLines.length} / ${totalLines}`}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                />
                            )}
                    </Stack>
                </Stack>

                {/* Log Level Filters */}
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                        label="Errors"
                        size="small"
                        onClick={() => {
                            const newFilters = new Set(activeFilters);
                            if (newFilters.has('error')) {
                                newFilters.delete('error');
                            } else {
                                newFilters.add('error');
                            }
                            setActiveFilters(newFilters);
                        }}
                        color={activeFilters.has('error') ? 'error' : 'default'}
                        variant={activeFilters.has('error') ? 'filled' : 'outlined'}
                    />
                    <Chip
                        label="Warnings"
                        size="small"
                        onClick={() => {
                            const newFilters = new Set(activeFilters);
                            if (newFilters.has('warning')) {
                                newFilters.delete('warning');
                            } else {
                                newFilters.add('warning');
                            }
                            setActiveFilters(newFilters);
                        }}
                        color={activeFilters.has('warning') ? 'warning' : 'default'}
                        variant={activeFilters.has('warning') ? 'filled' : 'outlined'}
                    />
                    <Chip
                        label="Info"
                        size="small"
                        onClick={() => {
                            const newFilters = new Set(activeFilters);
                            if (newFilters.has('info')) {
                                newFilters.delete('info');
                            } else {
                                newFilters.add('info');
                            }
                            setActiveFilters(newFilters);
                        }}
                        color={activeFilters.has('info') ? 'info' : 'default'}
                        variant={activeFilters.has('info') ? 'filled' : 'outlined'}
                    />
                    <Chip
                        label="Debug"
                        size="small"
                        onClick={() => {
                            const newFilters = new Set(activeFilters);
                            if (newFilters.has('debug')) {
                                newFilters.delete('debug');
                            } else {
                                newFilters.add('debug');
                            }
                            setActiveFilters(newFilters);
                        }}
                        color={activeFilters.has('debug') ? 'default' : 'default'}
                        variant={activeFilters.has('debug') ? 'filled' : 'outlined'}
                    />
                    <Chip
                        label="Other"
                        size="small"
                        onClick={() => {
                            const newFilters = new Set(activeFilters);
                            if (newFilters.has('default')) {
                                newFilters.delete('default');
                            } else {
                                newFilters.add('default');
                            }
                            setActiveFilters(newFilters);
                        }}
                        color={activeFilters.has('default') ? 'default' : 'default'}
                        variant={activeFilters.has('default') ? 'filled' : 'outlined'}
                    />
                </Stack>

                {/* Log Content */}
                {isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 400,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {LL.adminLogsPage.failedToLoad()}
                    </Alert>
                ) : logLines.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        {LL.adminLogsPage.noLogsAvailable()}
                    </Alert>
                ) : (
                    <Paper
                        sx={{
                            p: 0,
                            mb: 3,
                            bgcolor: '#1e1e1e',
                            color: '#d4d4d4',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            overflow: 'hidden',
                        }}
                    >
                        {filteredLines.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {LL.adminLogsPage.noMatchingLogs()}
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                ref={scrollContainerRef}
                                onScroll={handleScroll}
                                sx={{
                                    maxHeight: { xs: 500, sm: 700 },
                                    overflow: 'auto',
                                }}
                            >
                                {filteredLines.map((line, index) => {
                                    const level = getLogLevel(line);
                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                px: 2,
                                                py: 0.75,
                                                borderBottom:
                                                    index < filteredLines.length - 1
                                                        ? '1px solid rgba(255, 255, 255, 0.05)'
                                                        : 'none',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                bgcolor:
                                                    level === 'error'
                                                        ? 'rgba(244, 67, 54, 0.1)'
                                                        : level === 'warning'
                                                          ? 'rgba(255, 152, 0, 0.1)'
                                                          : 'transparent',
                                                color:
                                                    level === 'error'
                                                        ? '#f44336'
                                                        : level === 'warning'
                                                          ? '#ff9800'
                                                          : '#d4d4d4',
                                                '&:hover': {
                                                    bgcolor:
                                                        level === 'error'
                                                            ? 'rgba(244, 67, 54, 0.15)'
                                                            : level === 'warning'
                                                              ? 'rgba(255, 152, 0, 0.15)'
                                                              : 'rgba(255, 255, 255, 0.05)',
                                                },
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {line}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                                {!searchQuery && hasMoreLogs && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            py: 2,
                                        }}
                                    >
                                        <CircularProgress size={24} sx={{ color: '#d4d4d4' }} />
                                        <Typography
                                            variant="caption"
                                            sx={{ ml: 2, color: '#d4d4d4' }}
                                        >
                                            Loading more logs...
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Paper>
                )}

                {/* Pagination */}
                {!searchQuery && totalLines > LIMIT && (
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{ pb: 5, alignItems: 'center', justifyContent: 'center' }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Showing {allLogLines.length} of {totalLines} log entries
                        </Typography>
                    </Stack>
                )}
            </Box>
        </>
    );
};

export default AdminLogsPage;
